import { ArrowLeft, Check, Clock, Pencil, Phone, Save, Trash2, X as XIcon } from 'lucide-react';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
    deleteEmployee,
    getEmployee,
    getPermissionList,
    getShiftTypes,
    getUserById,
    updateEmployee,
    updateUser,
} from '../../../api/employee.api';
import styles from '../../../assets/scss/pages/EmployeeDetail.module.scss';
import { PERMISSIONS } from '../../../config/permissions';
import { usePermissions } from '../../../hooks/usePermissions';
import { formatCurrency, formatPhoneNumber } from '../../../utils/formatters';
import FormattedInput from '../../../components/common/FormattedInput';

const AVATAR_COLORS = ['#0098F6', '#22C55E', '#F59E0B', '#EF4444', '#8B5CF6', '#EC4899', '#14B8A6', '#F97316'];
function getAvatarColor(name) {
    let hash = 0;
    for (let i = 0; i < (name || '').length; i++) hash = name.charCodeAt(i) + ((hash << 5) - hash);
    return AVATAR_COLORS[Math.abs(hash) % AVATAR_COLORS.length];
}

const PERM_GROUP_LABELS = {
    customer: 'Quyền khách hàng', bill: 'Quyền đơn hàng và hóa đơn',
    service: 'Quyền sản phẩm', package: 'Gói dịch vụ',
    booking: 'Lịch hẹn - Đặt lịch', attribute: 'Quyền thuộc tính',
    commission: 'Quyền hoa hồng', feedback: 'Quyền đánh giá',
    report: 'Quyền báo cáo', revenue_book: 'Quyền sổ doanh thu',
    employee: 'Quyền nhân viên', shift_work: 'Quyền ca làm việc',
    salary: 'Quyền quản lý nhân sự',
};

const PERM_DEPS = {
    BILL_UPDATE: 'BILL_VIEW_DETAIL',
    SERVICE_UPDATE: 'SERVICE_VIEW_DETAIL',
    FEEDBACK_UPDATE_QR_STATUS: 'BILL_VIEW_LIST',
    EMPLOYEE_DETAIL: 'SHIFT_WORK_VIEW',
};

function MoneyInput({ value, onChange, placeholder }) {
    const inputRef = useRef(null);
    const displayValue = value ? Number(String(value).replace(/\D/g, '')).toLocaleString('vi-VN') : '';
    const handleChange = (e) => {
        const el = e.target;
        const prevVal = displayValue;
        const rawNew = el.value.replace(/\D/g, '');
        const num = rawNew ? parseInt(rawNew, 10) : 0;
        const newFormatted = num > 0 ? num.toLocaleString('vi-VN') : '';
        const cursorPos = el.selectionStart;
        const dotsBefore = (prevVal.slice(0, cursorPos).match(/\./g) || []).length;
        const rawCursorPos = cursorPos - dotsBefore;
        const dotsInNew = (pos) => {
            let count = 0, raw = 0;
            for (let i = 0; i < newFormatted.length && raw < pos; i++) {
                if (newFormatted[i] === '.') count++; else raw++;
            }
            return count;
        };
        const newCursor = rawCursorPos + dotsInNew(rawCursorPos);
        onChange(rawNew || '');
        requestAnimationFrame(() => {
            if (inputRef.current) inputRef.current.setSelectionRange(newCursor, newCursor);
        });
    };
    return (
        <input ref={inputRef} className={styles.formInput} value={displayValue}
            onChange={handleChange} placeholder={placeholder || '0'} inputMode="numeric" />
    );
}

function arraysEqual(a, b) {
    const sa = [...(a || [])].sort();
    const sb = [...(b || [])].sort();
    return sa.length === sb.length && sa.every((v, i) => v === sb[i]);
}

export default function EmployeeDetail() {
    const { id } = useParams();
    const navigate = useNavigate();
    const { hasPermission } = usePermissions();
    const canUpdate = hasPermission(PERMISSIONS.EMPLOYEE.UPDATE);
    const canDelete = hasPermission(PERMISSIONS.EMPLOYEE.DELETE);

    // Data
    const [employee, setEmployee] = useState(null);
    const [userData, setUserData] = useState(null);
    const [shiftTypes, setShiftTypes] = useState([]);
    const [permissionList, setPermissionList] = useState([]);
    const [initialPermCodes, setInitialPermCodes] = useState([]);

    // Edit state
    const [isEditing, setIsEditing] = useState(false);
    const [editForm, setEditForm] = useState({
        name: '', phone: '', baseSalary: '', allowance: '', status: 'active', shiftTypeId: '',
    });
    const [editAccount, setEditAccount] = useState({ userName: '', passWord: '' });
    const [selectedPerms, setSelectedPerms] = useState({});

    // UI
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [errors, setErrors] = useState({});
    const [toastMsg, setToastMsg] = useState(null);

    const showToast = useCallback((type, text, duration = 1500) => {
        setToastMsg({ type, text });
        setTimeout(() => setToastMsg(null), duration);
    }, []);

    // === Fetch all data ===
    const fetchData = useCallback(async () => {
        setLoading(true);
        try {
            const [empRes, userRes, shiftRes, permRes] = await Promise.all([
                getEmployee(id),
                getUserById(id),
                getShiftTypes(),
                getPermissionList(),
            ]);

            const emp = empRes.data.employee || empRes.data;
            const usr = userRes.data;
            const shifts = shiftRes.data.shiftTypes || [];
            const perms = permRes.data.data || [];

            setEmployee(emp);
            setUserData(usr);
            setShiftTypes(shifts);
            setPermissionList(perms);

            // Init edit form
            setEditForm({
                name: emp.name || '',
                phone: emp.phone || '',
                baseSalary: emp.baseSalary ? String(emp.baseSalary) : '',
                allowance: emp.allowance ? String(emp.allowance) : '',
                status: emp.status === true || emp.status === 'active' ? 'active' : 'inactive',
                shiftTypeId: emp.shiftTypeId || '',
            });
            setEditAccount({ userName: usr.userName || '', passWord: '' });

            // Init permissions — extract from multiple possible paths (like app)
            const codes = empRes.data?.permissionCodes || emp.permissionCodes || [];
            setInitialPermCodes(codes);
            const codeSet = new Set(codes);
            const map = {};
            perms.forEach((p) => { map[p.code] = codeSet.has(p.code); });
            setSelectedPerms(map);
        } catch (err) {
            console.error('Error fetching employee:', err);
        } finally {
            setLoading(false);
        }
    }, [id]);

    useEffect(() => { fetchData(); }, [fetchData]);

    const isAdmin = userData?.role === 'admin';
    const isEmployeeRole = userData?.role === 'employee';

    // === Permission toggle ===
    const handlePermToggle = (code) => {
        setSelectedPerms((prev) => {
            const next = { ...prev };
            const isEnabled = !next[code];
            next[code] = isEnabled;
            if (isEnabled) {
                const dep = PERM_DEPS[code];
                if (dep) next[dep] = true;
            } else {
                Object.entries(PERM_DEPS).forEach(([depCode, depOn]) => {
                    if (depOn === code && next[depCode]) next[depCode] = false;
                });
            }
            return next;
        });
    };

    // === Save ===
    const handleSave = async () => {
        if (!canUpdate) return;
        setSaving(true);
        setErrors({});
        let hasUpdate = false;

        try {
            // 1) Employee fields
            const payload = {};
            if (editForm.name !== (employee.name || '')) payload.name = editForm.name;
            if (editForm.phone.replace(/\D/g, '') !== (employee.phone || '').replace(/\D/g, ''))
                payload.phone = editForm.phone.replace(/\D/g, '');
            if (Number(editForm.baseSalary) !== Number(employee.baseSalary || 0))
                payload.baseSalary = Number(editForm.baseSalary) || 0;
            if (Number(editForm.allowance) !== Number(employee.allowance || 0))
                payload.allowance = Number(editForm.allowance) || 0;
            const origStatus = employee.status === true || employee.status === 'active' ? 'active' : 'inactive';
            if (editForm.status !== origStatus) payload.status = editForm.status;
            const origShift = employee.shiftTypeId || '';
            if (String(editForm.shiftTypeId) !== String(origShift))
                payload.shiftTypeId = editForm.shiftTypeId ? Number(editForm.shiftTypeId) : null;

            // Permissions (only for employee role)
            if (isEmployeeRole) {
                const currentCodes = Object.keys(selectedPerms).filter((c) => selectedPerms[c]);
                if (!arraysEqual(currentCodes, initialPermCodes)) {
                    payload.permissionCodes = currentCodes;
                }
            }

            if (Object.keys(payload).length > 0) {
                await updateEmployee(id, payload);
                hasUpdate = true;
            }

            // 2) Account fields
            if (!isAdmin) {
                const accPayload = {};
                const origUserName = (userData.userName || '').replace(/\D/g, '');
                const newUserName = editAccount.userName.replace(/\D/g, '');
                if (newUserName.length === 10 && newUserName !== origUserName) accPayload.userName = newUserName;
                if (editAccount.passWord.trim()) accPayload.passWord = editAccount.passWord;

                if (Object.keys(accPayload).length > 0) {
                    await updateUser(userData.id, accPayload);
                    hasUpdate = true;
                }
            }

            if (!hasUpdate) {
                showToast('error', 'Không có thay đổi để cập nhật', 2000);
                setSaving(false);
                return;
            }

            showToast('success', 'Cập nhật thành công!');
            setIsEditing(false);
            setTimeout(() => fetchData(), 1600);
        } catch (err) {
            const msg = err.response?.data?.message || 'Cập nhật thất bại';
            setErrors({ submit: msg });
        } finally {
            setSaving(false);
        }
    };

    // === Delete ===
    const handleDelete = async () => {
        if (!canDelete) return;
        if (!window.confirm('Bạn có chắc muốn xoá nhân viên này?')) return;
        try {
            await deleteEmployee(id);
            showToast('success', 'Xoá nhân viên thành công');
            setTimeout(() => navigate('/admin/employees'), 1200);
        } catch {
            showToast('error', 'Xoá thất bại', 2500);
        }
    };

    // === Phone format ===
    const formatPhoneDisplay = (val) => {
        const d = (val || '').replace(/\D/g, '').slice(0, 10);
        if (d.length <= 4) return d;
        if (d.length <= 7) return `${d.slice(0, 4)}.${d.slice(4)}`;
        return `${d.slice(0, 4)}.${d.slice(4, 7)}.${d.slice(7)}`;
    };

    const parsePhone = (typed) => typed.replace(/\D/g, '').slice(0, 10);

    // === Group permissions ===
    const permGroups = {};
    permissionList.forEach((p) => {
        const g = p.group || 'other';
        if (!permGroups[g]) permGroups[g] = [];
        permGroups[g].push(p);
    });

    const activePermCount = Object.values(selectedPerms).filter(Boolean).length;

    // === Loading ===
    if (loading) {
        return (
            <div className={styles.page}>
                <div className={styles.header}>
                    <div className={styles.headerLeft}>
                        <div className={styles.skeleton} style={{ width: 40, height: 40, borderRadius: 10 }} />
                        <div className={styles.skeleton} style={{ width: 200, height: 28, borderRadius: 8 }} />
                    </div>
                </div>
                <div className={styles.layout}>
                    <div className={styles.layoutLeft}>
                        <div className={styles.card}>
                            <div className={styles.cardBody}>
                                <div style={{ display: 'flex', gap: 20 }}>
                                    <div className={styles.skeleton} style={{ width: 64, height: 64, borderRadius: 999 }} />
                                    <div style={{ flex: 1 }}>
                                        <div className={styles.skeleton} style={{ width: 180, height: 24, borderRadius: 8, marginBottom: 10 }} />
                                        <div className={styles.skeleton} style={{ width: 120, height: 20, borderRadius: 14 }} />
                                    </div>
                                </div>
                            </div>
                        </div>
                        <div className={styles.card}>
                            <div className={styles.cardHeader}><div className={styles.skeleton} style={{ width: 120, height: 20, borderRadius: 6 }} /></div>
                            <div className={styles.cardBody}>
                                <div className={styles.infoGrid}>
                                    {[1, 2, 3, 4].map((i) => (
                                        <div key={i} className={styles.infoRow}>
                                            <div className={styles.skeleton} style={{ width: 80, height: 12, borderRadius: 6 }} />
                                            <div className={styles.skeleton} style={{ width: '70%', height: 18, borderRadius: 6, marginTop: 4 }} />
                                        </div>
                                    ))}
                                </div>
                            </div>
                        </div>
                    </div>
                    <div className={styles.layoutRight}>
                        <div className={styles.card}>
                            <div className={styles.cardHeader}><div className={styles.skeleton} style={{ width: 100, height: 20, borderRadius: 6 }} /></div>
                            <div className={styles.cardBody}>
                                {[1, 2, 3].map((i) => (
                                    <div key={i} style={{ marginBottom: 16 }}>
                                        <div className={styles.skeleton} style={{ width: 120, height: 14, borderRadius: 6, marginBottom: 8 }} />
                                        <div className={styles.skeleton} style={{ width: '100%', height: 14, borderRadius: 6 }} />
                                    </div>
                                ))}
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        );
    }

    if (!employee) {
        return <div className={styles.page} style={{ textAlign: 'center', padding: 60, color: '#6B7280' }}>Không tìm thấy nhân viên</div>;
    }

    const empStatus = employee.status === true || employee.status === 'active' ? 'active' : 'inactive';
    const shiftType = shiftTypes.find((s) => s.id === employee.shiftTypeId);
    const hireDate = employee.hireDate ? new Date(employee.hireDate).toLocaleDateString('vi-VN') : '—';

    return (
        <div className={styles.page}>
            {/* Header */}
            <div className={styles.header}>
                <div className={styles.headerLeft}>
                    <button className={styles.backBtn} onClick={() => navigate(-1)}>
                        <ArrowLeft />
                    </button>
                    <h1 className={styles.title}>Chi tiết nhân viên</h1>
                </div>
                <div className={styles.headerActions}>
                    {canUpdate && !isEditing && (
                        <button className={`${styles.actionBtn} ${styles.actionBtnPrimary}`} onClick={() => setIsEditing(true)}>
                            <Pencil /> Chỉnh sửa
                        </button>
                    )}
                    {isEditing && (
                        <>
                            <button className={styles.actionBtn} onClick={() => { setIsEditing(false); fetchData(); }}>
                                <XIcon /> Huỷ
                            </button>
                            <button className={`${styles.actionBtn} ${styles.actionBtnPrimary}`} onClick={handleSave} disabled={saving}>
                                <Save /> {saving ? 'Đang lưu...' : 'Lưu'}
                            </button>
                        </>
                    )}
                    {isEditing && canDelete && !isAdmin && (
                        <button className={`${styles.actionBtn} ${styles.actionBtnDanger}`} onClick={handleDelete}>
                            <Trash2 /> Xoá
                        </button>
                    )}
                </div>
            </div>

            {errors.submit && <div className={styles.errorMsg}>{errors.submit}</div>}

            <div className={styles.layout}>
                {/* ===== CỘT TRÁI ===== */}
                <div className={styles.layoutLeft}>
                    {/* Hero */}
                    <div className={styles.card}>
                        <div className={styles.cardBody}>
                            <div className={styles.hero}>
                                <div className={styles.heroAvatar} style={{ background: getAvatarColor(employee.name) }}>
                                    {(employee.name || '?')[0].toUpperCase()}
                                </div>
                                <div className={styles.heroInfo}>
                                    <div className={styles.heroName}>{employee.name}</div>
                                    <div className={styles.heroMeta}>
                                        <span className={`${styles.badge} ${empStatus === 'active' ? styles.badgeGreen : styles.badgeGray}`}>
                                            {empStatus === 'active' ? 'Đang làm việc' : 'Ngừng làm việc'}
                                        </span>
                                        {isAdmin && <span className={`${styles.badge} ${styles.badgeBlue}`}>Boss</span>}
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* View mode */}
                    {!isEditing ? (
                        <>
                            <div className={styles.card}>
                                <div className={styles.cardHeader}>Thông tin</div>
                                <div className={styles.cardBody}>
                                    <div className={styles.infoGrid}>
                                        <div className={styles.infoRow}>
                                            <span className={styles.infoLabel}>Số điện thoại</span>
                                            <span className={styles.infoValue}>
                                                <Phone size={13} style={{ marginRight: 4, verticalAlign: -2 }} />
                                                {employee.phone ? formatPhoneNumber(employee.phone) : 'Chưa có'}
                                            </span>
                                        </div>
                                        <div className={styles.infoRow}>
                                            <span className={styles.infoLabel}>Ngày vào làm</span>
                                            <span className={styles.infoValue}>{hireDate}</span>
                                        </div>
                                        {!isAdmin && (
                                            <>
                                                <div className={styles.infoRow}>
                                                    <span className={styles.infoLabel}>Thu nhập cơ bản</span>
                                                    <span className={styles.infoValueMoney}>{formatCurrency(employee.baseSalary || 0)}</span>
                                                </div>
                                                <div className={styles.infoRow}>
                                                    <span className={styles.infoLabel}>Phụ cấp</span>
                                                    <span className={styles.infoValueMoney}>{formatCurrency(employee.allowance || 0)}</span>
                                                </div>
                                                <div className={styles.infoRow}>
                                                    <span className={styles.infoLabel}>Ca làm việc</span>
                                                    <span className={styles.infoValue}>
                                                        <Clock size={13} style={{ marginRight: 4, verticalAlign: -2 }} />
                                                        {shiftType
                                                            ? `${shiftType.name} (${(shiftType.startTime || '').slice(0, 5)} - ${(shiftType.endTime || '').slice(0, 5)})`
                                                            : 'Chưa có'}
                                                    </span>
                                                </div>
                                            </>
                                        )}
                                    </div>
                                </div>
                            </div>

                            {/* Account info */}
                            {!isAdmin && userData && (
                                <div className={styles.card}>
                                    <div className={styles.cardHeader}>Tài khoản</div>
                                    <div className={styles.cardBody}>
                                        <div className={styles.accountRow}>
                                            <span className={styles.accountLabel}>Tên đăng nhập</span>
                                            <span className={styles.accountValue}>{formatPhoneDisplay(userData.userName || '')}</span>
                                        </div>
                                        <div className={styles.accountRow}>
                                            <span className={styles.accountLabel}>Mật khẩu</span>
                                            <span className={styles.accountValue}>*****</span>
                                        </div>
                                    </div>
                                </div>
                            )}
                        </>
                    ) : (
                        /* Edit mode */
                        <>
                            <div className={styles.card}>
                                <div className={styles.cardHeader}>Chỉnh sửa thông tin</div>
                                <div className={styles.cardBody}>
                                    <div className={styles.formGrid}>
                                        <div className={styles.formGroup}>
                                            <label className={styles.formLabel}>Họ tên</label>
                                            <input className={styles.formInput} value={editForm.name}
                                                onChange={(e) => setEditForm((p) => ({ ...p, name: e.target.value }))} />
                                        </div>
                                        {!isAdmin && (
                                            <div className={styles.formGroup}>
                                                <label className={styles.formLabel}>Số điện thoại</label>
                                                <FormattedInput className={styles.formInput} value={editForm.phone}
                                                    format={formatPhoneDisplay} parse={parsePhone}
                                                    onChange={(raw) => setEditForm((p) => ({ ...p, phone: raw }))}
                                                    inputMode="numeric" />
                                            </div>
                                        )}
                                        {!isAdmin && (
                                            <>
                                                <div className={styles.formGroup}>
                                                    <label className={styles.formLabel}>Thu nhập cơ bản</label>
                                                    <MoneyInput value={editForm.baseSalary} onChange={(v) => setEditForm((p) => ({ ...p, baseSalary: v }))} />
                                                </div>
                                                <div className={styles.formGroup}>
                                                    <label className={styles.formLabel}>Phụ cấp</label>
                                                    <MoneyInput value={editForm.allowance} onChange={(v) => setEditForm((p) => ({ ...p, allowance: v }))} />
                                                </div>
                                                <div className={styles.formGroup}>
                                                    <label className={styles.formLabel}>Ca làm việc</label>
                                                    <select className={styles.formSelect} value={editForm.shiftTypeId}
                                                        onChange={(e) => setEditForm((p) => ({ ...p, shiftTypeId: e.target.value }))}>
                                                        <option value="">Chưa có ca</option>
                                                        {shiftTypes.map((s) => (
                                                            <option key={s.id} value={s.id}>
                                                                {s.name} ({(s.startTime || '').slice(0, 5)} - {(s.endTime || '').slice(0, 5)})
                                                            </option>
                                                        ))}
                                                    </select>
                                                </div>
                                                <div className={styles.formGroup}>
                                                    <label className={styles.formLabel}>Trạng thái</label>
                                                    <div className={styles.statusOptions}>
                                                        <button
                                                            className={`${styles.statusOption}${editForm.status === 'active' ? ` ${styles.statusOptionActive}` : ''}`}
                                                            onClick={() => setEditForm((p) => ({ ...p, status: 'active' }))}
                                                        >Đang làm</button>
                                                        <button
                                                            className={`${styles.statusOption}${editForm.status === 'inactive' ? ` ${styles.statusOptionInactive}` : ''}`}
                                                            onClick={() => setEditForm((p) => ({ ...p, status: 'inactive' }))}
                                                        >Ngừng làm</button>
                                                    </div>
                                                </div>
                                            </>
                                        )}
                                    </div>
                                </div>
                            </div>

                            {/* Edit account */}
                            {!isAdmin && (
                                <div className={styles.card}>
                                    <div className={styles.cardHeader}>Tài khoản đăng nhập</div>
                                    <div className={styles.cardBody}>
                                        <div className={styles.formGrid}>
                                            <div className={styles.formGroup}>
                                                <label className={styles.formLabel}>Tên đăng nhập (SĐT)</label>
                                                <FormattedInput className={styles.formInput} value={editAccount.userName}
                                                    format={formatPhoneDisplay} parse={parsePhone}
                                                    onChange={(raw) => setEditAccount((p) => ({ ...p, userName: raw }))}
                                                    inputMode="numeric" />
                                            </div>
                                            <div className={styles.formGroup}>
                                                <label className={styles.formLabel}>Mật khẩu mới</label>
                                                <input className={styles.formInput} type="password" value={editAccount.passWord}
                                                    onChange={(e) => setEditAccount((p) => ({ ...p, passWord: e.target.value }))}
                                                    placeholder="Để trống nếu không đổi" />
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            )}
                        </>
                    )}
                </div>

                {/* ===== CỘT PHẢI: Quyền ===== */}
                <div className={styles.layoutRight}>
                    <div className={styles.card}>
                        <div className={styles.cardHeader}>
                            <span>Quyền truy cập</span>
                            {isEmployeeRole && (
                                <span className={styles.permBadge}>{activePermCount}/{permissionList.length}</span>
                            )}
                        </div>
                        <div className={styles.cardBody}>
                            {isAdmin ? (
                                <div className={styles.permNote}>Admin có toàn quyền hệ thống</div>
                            ) : !isEmployeeRole ? (
                                <div className={styles.permNote}>Quản lý có toàn quyền như chủ cửa hàng</div>
                            ) : !isEditing ? (
                                /* View permissions */
                                Object.entries(permGroups).map(([group, perms]) => (
                                    <div key={group} className={styles.permGroup}>
                                        <div className={styles.permGroupTitle}>{PERM_GROUP_LABELS[group] || group}</div>
                                        {perms.map((p) => (
                                            <div key={p.code} className={`${styles.permItem} ${selectedPerms[p.code] ? styles.permItemEnabled : styles.permItemDisabled}`}>
                                                {selectedPerms[p.code] ? <Check size={14} /> : <XIcon size={14} />}
                                                {p.name}
                                            </div>
                                        ))}
                                    </div>
                                ))
                            ) : (
                                /* Edit permissions */
                                Object.entries(permGroups).map(([group, perms]) => (
                                    <div key={group} className={styles.permGroup}>
                                        <div className={styles.permGroupTitle}>{PERM_GROUP_LABELS[group] || group}</div>
                                        {perms.map((p) => (
                                            <label key={p.code} className={styles.permCheckbox}>
                                                <input type="checkbox" checked={!!selectedPerms[p.code]}
                                                    onChange={() => handlePermToggle(p.code)} />
                                                {p.name}
                                            </label>
                                        ))}
                                    </div>
                                ))
                            )}
                        </div>
                    </div>
                </div>
            </div>

            {toastMsg && (
                <div className={styles.successOverlay}>
                    <div className={styles.successModal}>
                        <div className={styles.successIcon} style={toastMsg.type === 'error' ? { background: '#EF4444' } : undefined}>
                            {toastMsg.type === 'success' ? '✓' : '✕'}
                        </div>
                        <span className={styles.successText}>{toastMsg.text}</span>
                    </div>
                </div>
            )}
        </div>
    );
}
